// Bomb Tag / Hot Potato 30Hz Arena Server Engine (2-8 Players)
export class BombTagEngine {
  constructor(io, room) {
    this.io = io;
    this.room = room;
    this.width = 800;
    this.height = 520;
    this.players = {};
    this.bombHolderId = null;
    this.bombFuse = 15;
    this.loopInterval = null;
    this.obstacles = [
      { x: 180, y: 120, w: 60, h: 60 },
      { x: 560, y: 120, w: 60, h: 60 },
      { x: 360, y: 220, w: 80, h: 80 },
      { x: 180, y: 340, w: 60, h: 60 },
      { x: 560, y: 340, w: 60, h: 60 }
    ];
  }

  start() {
    this.players = {};
    const targetCount = Math.min(8, Math.max(2, this.room.settings?.targetPlayers || 4));
    const shouldFillBots = this.room.settings?.fillBots !== false;

    const colors = ['#ef4444', '#38bdf8', '#22c55e', '#facc15', '#a855f7', '#ec4899', '#f97316', '#06b6d4'];
    const humanPlayers = this.room.players.slice(0, targetCount);

    humanPlayers.forEach((p, idx) => {
      this.initPlayer(p.id, p.name, colors[idx % colors.length], !!p.isBot);
    });

    if (shouldFillBots) {
      const bots = [
        { id: 'bot_runner1', name: 'Runner Bot 🏃', color: '#facc15' },
        { id: 'bot_runner2', name: 'Flash Bot ⚡', color: '#ec4899' },
        { id: 'bot_runner3', name: 'Dodge Bot 🛡️', color: '#a855f7' }
      ];
      for (let i = humanPlayers.length; i < targetCount; i++) {
        const b = bots[(i - humanPlayers.length) % bots.length];
        this.initPlayer(`${b.id}_${i}`, b.name, b.color, true);
      }
    }

    // Pick first bomb holder
    const activeIds = Object.keys(this.players);
    this.bombHolderId = activeIds[Math.floor(Math.random() * activeIds.length)];
    this.bombFuse = 15;

    this.io.to(this.room.code).emit('bt_started', {
      players: this.players,
      bombHolderId: this.bombHolderId,
      bombFuse: this.bombFuse,
      obstacles: this.obstacles
    });

    if (this.loopInterval) clearInterval(this.loopInterval);
    this.loopInterval = setInterval(() => this.update(), 1000 / 30);
  }

  initPlayer(id, name, color, isBot = false) {
    this.players[id] = {
      id,
      name,
      color,
      x: 100 + Math.random() * (this.width - 200),
      y: 100 + Math.random() * (this.height - 200),
      vx: 0,
      vy: 0,
      input: { up: false, down: false, left: false, right: false },
      isAlive: true,
      isBot,
      tagCooldown: 0
    };
  }

  handleInput(playerId, input) {
    const p = this.players[playerId];
    if (p && p.isAlive) {
      p.input = input;
    }
  }

  update() {
    this.bombFuse -= 1 / 30;

    const aliveList = Object.values(this.players).filter(p => p.isAlive);

    // Update positions
    for (const id in this.players) {
      const p = this.players[id];
      if (!p.isAlive) continue;

      if (p.tagCooldown > 0) p.tagCooldown--;

      const speed = id === this.bombHolderId ? 5.8 : 5.0; // Bomb holder runs slightly faster!

      // Bot AI
      if (p.isBot) {
        const holder = this.players[this.bombHolderId];
        if (id === this.bombHolderId) {
          // Chase closest player
          let closest = null;
          let minDist = 999;
          aliveList.filter(o => o.id !== id).forEach(o => {
            const d = Math.hypot(o.x - p.x, o.y - p.y);
            if (d < minDist) { minDist = d; closest = o; }
          });
          if (closest) {
            p.vx = (closest.x > p.x ? 1 : -1) * speed;
            p.vy = (closest.y > p.y ? 1 : -1) * speed;
          }
        } else if (holder) {
          // Flee from bomb holder
          p.vx = (p.x > holder.x ? 1 : -1) * speed;
          p.vy = (p.y > holder.y ? 1 : -1) * speed;
        }
      } else {
        p.vx = (p.input.right ? speed : 0) - (p.input.left ? speed : 0);
        p.vy = (p.input.down ? speed : 0) - (p.input.up ? speed : 0);
      }

      let nextX = Math.max(20, Math.min(this.width - 20, p.x + p.vx));
      let nextY = Math.max(20, Math.min(this.height - 20, p.y + p.vy));

      // Obstacle Collisions
      for (const ob of this.obstacles) {
        if (nextX > ob.x - 14 && nextX < ob.x + ob.w + 14 && nextY > ob.y - 14 && nextY < ob.y + ob.h + 14) {
          nextX = p.x;
          nextY = p.y;
          break;
        }
      }

      p.x = nextX;
      p.y = nextY;
    }

    // Tag Collision Check
    const holder = this.players[this.bombHolderId];
    if (holder && holder.isAlive && holder.tagCooldown === 0) {
      for (const other of aliveList) {
        if (other.id === holder.id) continue;
        if (Math.hypot(holder.x - other.x, holder.y - other.y) < 26) {
          // Tagged! Pass bomb
          this.bombHolderId = other.id;
          other.tagCooldown = 40; // 1.3s tag back cooldown
          this.io.to(this.room.code).emit('bt_bombPassed', {
            newHolderId: other.id,
            newHolderName: other.name
          });
          break;
        }
      }
    }

    // Fuse Expiry / Detonation
    if (this.bombFuse <= 0) {
      if (holder) {
        holder.isAlive = false;
        this.io.to(this.room.code).emit('bt_exploded', {
          eliminatedId: holder.id,
          eliminatedName: holder.name
        });

        const remaining = Object.values(this.players).filter(p => p.isAlive);
        if (remaining.length <= 1) {
          clearInterval(this.loopInterval);
          this.io.to(this.room.code).emit('bt_gameOver', {
            winner: remaining[0] || holder
          });
          return;
        } else {
          this.bombHolderId = remaining[Math.floor(Math.random() * remaining.length)].id;
          this.bombFuse = 14;
        }
      }
    }

    this.io.to(this.room.code).emit('bt_state', {
      players: this.players,
      bombHolderId: this.bombHolderId,
      bombFuse: Math.max(0, Math.ceil(this.bombFuse))
    });
  }

  destroy() {
    if (this.loopInterval) clearInterval(this.loopInterval);
  }
}
